import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useClearableFilters } from '@fukuri-admins/utils/common';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createClientAdminUser, getClientAdminUsers } from '../../../api/client_admin_users';
import { getPartnerFeatureFlags } from '../../../api/partner_feature_flags';
import { useAuth } from '../../../components/auth/AuthProvider.tsx';
import { useConfiguration } from '../../../contexts/ConfigurationProvider';
import { NewClientAdminUserFormType } from '../../../types/forms/NewClientAdminUserFormType.ts';
import { ClientAdminUserType } from '../../../types/resources/ClientAdminUserType.ts';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType.ts';
import { getClientAdminUsersLink } from '../../../utils/getLinks.ts';
import { IntegratedClientAdminUserList } from './IntegratedClientAdminUserList.tsx';
import { StandardClientAdminUserList } from './StandardClientAdminUserList.tsx';
import styles from './styles.module.scss';

export const IndexClientAdminUser = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { applicationConfiguration } = useConfiguration();
  const [filterParams, setFilterParams] = useSearchParams();
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredClientAdminUsers, setFilteredClientAdminUsers] = useState<ClientAdminUserType[]>(
    []
  );
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [addClientStatus, setAddClientStatus] = useState(false);
  const [addClientFinishStatus, setAddClientFinishStatus] = useState(false);
  const [errorMessageModalStatus, setErrorMessageModalStatus] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const { showToast } = useToast();
  const [creditEvaluationFlag, setCreditEvaluationFlag] = useState<boolean>(false);
  const [lastAddedClientAdminUserEmail, setLastAddedClientAdminUserEmail] = useState<string>('');

  const handleAddClientAdminUser = async (data: NewClientAdminUserFormType) => {
    try {
      const newClientAdminUser = await createClientAdminUser({
        client_admin_user: data,
      });
      const newProcessedClientAdminUser = {
        id: newClientAdminUser.client_admin_user.id,
        client: newClientAdminUser.client_admin_user.client,
        full_name: newClientAdminUser.client_admin_user.full_name || '',
        email: newClientAdminUser.client_admin_user.email || '',
        external_client_admin_user_id:
          newClientAdminUser.client_admin_user.external_client_admin_user_id || '',
        created_at: newClientAdminUser.client_admin_user.created_at || 0,
      };
      setFilteredClientAdminUsers(prevFilteredClientAdminUsers => [
        newProcessedClientAdminUser,
        ...prevFilteredClientAdminUsers,
      ]);
      setErrMsg('');
      setLastAddedClientAdminUserEmail(newProcessedClientAdminUser.email);
      setAddClientFinishStatus(true);
      setAddClientStatus(false);
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Unknown error occurred.';
      const errorDetails = error?.details || 'No additional details provided.';

      setErrMsg(`Error adding client admin user:\n${errorMessage}\nDetails: ${errorDetails}`);
      setErrorMessageModalStatus(true);
      return false;
    }
  };

  function handleErrorMessageModal() {
    setErrorMessageModalStatus(false);
  }

  function handleAddClientFinish() {
    setAddClientFinishStatus(false);
    navigate(getClientAdminUsersLink());
  }

  function handlePreviousRoute() {
    setAddClientStatus(false);
    navigate(getClientAdminUsersLink());
  }

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    getClientsAdminData(page);
  };

  const onSearch = () => {
    setIsLoading(true);
    getClientsAdminData();
  };

  const onClear = () => {
    setIsLoading(true);
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('full_name');
    newParams.delete('client_name');
    newParams.delete('page');
    setFilterParams(newParams);
    setIsClearFilter(true);
  };

  const getClientsAdminData = async (page?: number) => {
    try {
      const full_name = filterParams.get('full_name');
      const client_name = filterParams.get('client_name');
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getClientAdminUsers(signOut, {
        ...(full_name && { full_name }),
        ...(client_name && { client_name }),
        ...(pageNumber && { page: pageNumber.toString() }),
      });
      setTotalPage(Number(headers['total-pages']));
      setTotalCount(Number(headers['total-count']));
      const clientAdminUsersData = data.client_admin_users;
      const processedClientAdminUsers = clientAdminUsersData.map(
        (clientAdminUser: ClientAdminUserType) => {
          return {
            id: clientAdminUser.id,
            client: clientAdminUser.client,
            full_name: clientAdminUser.full_name || '',
            email: clientAdminUser.email || '',
            external_client_admin_user_id: clientAdminUser.external_client_admin_user_id || '',
            created_at: clientAdminUser.created_at || 0,
          };
        }
      );
      setFilteredClientAdminUsers(processedClientAdminUsers);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const { setIsClearFilter, filterKey } = useClearableFilters({
    filterParams,
    fetchFunction: getClientsAdminData,
  });

  useEffect(() => {
    getClientsAdminData();
  }, []);

  useEffect(() => {
    const fetchPartnerFeatureFlags = async () => {
      const data = await getPartnerFeatureFlags();
      const creditEvaluationFlag = data.partner_feature_flags.find(
        flag => flag.feature_key === 'credit_evaluation'
      );
      setCreditEvaluationFlag(creditEvaluationFlag?.enabled ?? false);
    };
    fetchPartnerFeatureFlags();
  }, []);

  return (
    <div className={styles.Root} data-testid='client-admin-page'>
      {applicationConfiguration.application_configuration.is_api_integration ? (
        <IntegratedClientAdminUserList
          filteredClientAdminUsers={filteredClientAdminUsers}
          setFilteredClientAdminUsers={setFilteredClientAdminUsers}
          isLoading={isLoading}
          sortParams={sortParams}
          setSortParams={setSortParams}
          handlePageChange={handlePageChange}
          totalCount={totalCount}
          totalPage={totalPage}
          onSearch={onSearch}
          onClear={onClear}
          key={filterKey}
        />
      ) : (
        <StandardClientAdminUserList
          filteredClientAdminUsers={filteredClientAdminUsers}
          setFilteredClientAdminUsers={setFilteredClientAdminUsers}
          isLoading={isLoading}
          sortParams={sortParams}
          setSortParams={setSortParams}
          handlePageChange={handlePageChange}
          totalCount={totalCount}
          totalPage={totalPage}
          setAddClientStatus={setAddClientStatus}
          addClientFinishStatus={addClientFinishStatus}
          setAddClientFinishStatus={setAddClientFinishStatus}
          handlePreviousRoute={handlePreviousRoute}
          handleAddClientAdminUser={handleAddClientAdminUser}
          handleAddClientFinish={handleAddClientFinish}
          handleErrorMessageModal={handleErrorMessageModal}
          errorMessageModalStatus={errorMessageModalStatus}
          setErrorMessageModalStatus={setErrorMessageModalStatus}
          errMsg={errMsg}
          addClientStatus={addClientStatus}
          creditEvaluationFlag={creditEvaluationFlag}
          lastAddedClientAdminUserEmail={lastAddedClientAdminUserEmail}
          onSearch={onSearch}
          onClear={onClear}
          key={filterKey}
        />
      )}
    </div>
  );
};
